import axios from 'axios';

/**
 * API Service
 * Handles HTTP requests with standard error handling, authentication, and response formatting
 */
class ApiService {
  constructor() {
    // Ensure we don't have duplicate /api in the URL
    // Get dynamic port from backend or fallback to default
    // The Python backend runs on port 8003 in production (check .env files)
    const port = process.env.REACT_APP_API_PORT || '8003';
    let baseURL = process.env.REACT_APP_API_URL || `http://localhost:${port}/api`;
    
    this.api = axios.create({
      baseURL,
      timeout: 600000, // 10 minutes (increased from 30 seconds)
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor for auth
    this.api.interceptors.request.use(
      (config) => {
        // Add token to headers if available
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('API: No auth token found for request:', config.url);
          console.warn('API: localStorage keys:', Object.keys(localStorage));
        }
        
        // Don't set Content-Type for FormData (let the browser set it with boundary)
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        
        // Check for duplicate /api in URL and fix it
        if (config.url && config.url.startsWith('/api/') && config.baseURL.endsWith('/api')) {
          config.url = config.url.substring(4); // Remove the /api prefix
        }

        // Add trailing slash to API paths to prevent 307 redirects
        // (307 redirects strip the Authorization header in browsers)
        if (config.url) {
          const [path, query] = config.url.split('?');
          if (path && !path.endsWith('/') && !path.includes('.')) {
            config.url = path + '/' + (query ? '?' + query : '');
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Handle token expiration
        if (error.response && error.response.status === 401) {
          // Don't clear token or redirect on test pages - let them handle 401 errors
          const isTestPage = window.location.pathname.includes('/test-oauth') ||
                            window.location.pathname.includes('/oauth/callback');

          if (!isTestPage && window.location.pathname !== '/login') {
            // Clear token and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('tshirt_size_provided');

            // Only redirect if not in a login-related page
            if (
              !window.location.pathname.includes('/login') &&
              !window.location.pathname.includes('/register') &&
              !window.location.pathname.includes('/forgot-password')
            ) {
              window.location.href = '/login?session=expired';
            }
          }
        }
        
        // Format error for consistent handling
        let errorMessage = 'An unexpected error occurred';
        let errorData = null;
        
        if (error.response) {
          // Server responded with a status code outside 2xx range
          errorMessage = error.response.data.message || error.response.data.detail || error.response.statusText;
          errorData = error.response.data;
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = 'No response received from server';
        } else {
          // Something happened in setting up the request
          errorMessage = error.message;
        }
        
        const formattedError = new Error(errorMessage);
        formattedError.data = errorData;
        formattedError.status = error.response ? error.response.status : null;
        
        return Promise.reject(formattedError);
      }
    );
  }

  /**
   * Make a GET request
   * @param {string} url - API endpoint
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async get(url, config = {}) {
    try {
      return await this.api.get(url, config);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a POST request with improved error handling and timeout management
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async post(url, data = {}, config = {}) {
    try {
      // Create a timeout promise that will reject after specified time
      const timeoutDuration = config.timeout || 30000; // Default to 30 seconds if not specified
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Request to ${url} timed out after ${timeoutDuration}ms`));
        }, timeoutDuration);
      });

      // Race the actual request against the timeout
      const requestPromise = this.api.post(url, data, config);
      const response = await Promise.race([requestPromise, timeoutPromise]);

      return response;
    } catch (error) {
      // Enhanced error handling
      if (error.message?.includes('timed out')) {
        console.error(`POST request to ${url} timed out:`, error);

        // Create a more user-friendly response instead of throwing
        return {
          data: {
            success: false,
            message: 'Request timed out. The server may be busy or unavailable.',
            error: error.message
          },
          status: 408 // Request Timeout status
        };
      }

      if (!error.response) {
        console.error(`POST request to ${url} failed with no response:`, error);

        // Create a response-like object for network errors
        return {
          data: {
            success: false,
            message: 'Network error or server unreachable.',
            error: error.message
          },
          status: 0 // No HTTP status
        };
      }

      throw error;
    }
  }

  /**
   * Make a PUT request
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async put(url, data = {}, config = {}) {
    try {
      return await this.api.put(url, data, config);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a PATCH request
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async patch(url, data = {}, config = {}) {
    try {
      return await this.api.patch(url, data, config);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make a DELETE request
   * @param {string} url - API endpoint
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async delete(url, config = {}) {
    try {
      return await this.api.delete(url, config);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload a file
   * @param {string} url - API endpoint
   * @param {FormData} formData - Form data with file
   * @param {Function} onProgress - Progress callback (optional)
   * @param {Object} config - Additional configuration
   * @returns {Promise} - API response
   */
  async uploadFile(url, formData, onProgress = null, config = {}) {
    try {
      const uploadConfig = {
      
        headers: {
         
          // Content-Type header will be removed for FormData in the interceptor
        }
      };
      
      // Add progress tracking if callback provided
      if (onProgress) {
        uploadConfig.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        };
      }
      
      return await this.api.post(url, formData, uploadConfig);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle streaming responses
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Function} onChunk - Chunk callback
   * @param {Function} onComplete - Completion callback
   * @param {Function} onError - Error callback
   * @param {Object} config - Additional configuration
   */
  async stream(url, data = {}, onChunk, onComplete, onError, config = {}) {
    try {
      const response = await this.api.post(url, data, {
        ...config,
        responseType: 'stream',
        onDownloadProgress: (progressEvent) => {
          const chunk = progressEvent.currentTarget.response;
          if (chunk) {
            onChunk(chunk);
          }
        }
      });
      
      // Call completion callback when done
      onComplete(response.data);
    } catch (error) {
      onError(error);
    }
  }

  /**
   * Set the auth token for all future requests
   * @param {string} token - JWT token
   */
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common.Authorization = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete this.api.defaults.headers.common.Authorization;
      localStorage.removeItem('token');
    }
  }

  /**
   * Clear the auth token and any stored session data
   */
  clearAuth() {
    delete this.api.defaults.headers.common.Authorization;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tshirt_size_provided');
  }
}

// Export a singleton instance of the API service
const apiService = new ApiService();
export default apiService.api; // For backwards compatibility
export const api = apiService.api;
export const apiHelpers = apiService;